/**
 * Select (U5): custom listbox with full keyboard support — arrows, Home/End,
 * Enter/Space, Escape, and first-letter type-ahead. ARIA combobox pattern.
 */
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type JSX,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { cx } from './cx';
import { Field } from './Field';
import { IconChevronDown } from './icons';
import type { ControlSize } from './Button';

export interface SelectOption<V extends string = string> {
  value: V;
  label: string;
  /** Right-aligned secondary text (rate, unit, count…). */
  hint?: string;
  disabled?: boolean;
}

export interface SelectProps<V extends string = string> {
  value: V | null;
  onChange: (value: V) => void;
  options: ReadonlyArray<SelectOption<V>>;
  placeholder?: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  size?: ControlSize;
  disabled?: boolean;
  required?: boolean;
  full?: boolean;
  id?: string;
  'aria-label'?: string;
}

export function Select<V extends string = string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  label,
  hint,
  error,
  size = 'md',
  disabled,
  required,
  full,
  id,
  'aria-label': ariaLabel,
}: SelectProps<V>): JSX.Element {
  const autoId = useId();
  const triggerId = id ?? autoId;
  const listboxId = `${triggerId}-listbox`;
  const describedById = hint != null || error ? `${triggerId}-desc` : undefined;

  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef(new Map<number, HTMLLIElement>());
  const typeahead = useRef({ buffer: '', at: 0 });

  const selectedIndex = useMemo(
    () => options.findIndex((o) => o.value === value),
    [options, value],
  );
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open && active >= 0) {
      optionRefs.current.get(active)?.scrollIntoView({ block: 'nearest' });
    }
  }, [open, active]);

  function openList(): void {
    if (disabled) return;
    setActive(selectedIndex >= 0 ? selectedIndex : firstEnabled(0, 1));
    setOpen(true);
  }

  function firstEnabled(from: number, dir: 1 | -1): number {
    for (let i = from; i >= 0 && i < options.length; i += dir) {
      if (!options[i]?.disabled) return i;
    }
    return -1;
  }

  function move(dir: 1 | -1): void {
    const start = active === -1 ? (dir === 1 ? 0 : options.length - 1) : active + dir;
    const next = firstEnabled(start, dir);
    if (next !== -1) setActive(next);
  }

  function choose(index: number): void {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    setOpen(false);
  }

  function handleTypeahead(char: string): void {
    const now = Date.now();
    const state = typeahead.current;
    state.buffer = now - state.at > 500 ? char : state.buffer + char;
    state.at = now;
    const query = state.buffer.toLowerCase();
    const startAt = active >= 0 ? active : 0;
    for (let offset = 0; offset < options.length; offset += 1) {
      const i = (startAt + offset + (state.buffer.length === 1 ? 1 : 0)) % options.length;
      const option = options[i];
      if (option && !option.disabled && option.label.toLowerCase().startsWith(query)) {
        if (open) setActive(i);
        else onChange(option.value);
        return;
      }
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
    if (disabled) return;
    const { key } = event;
    if (!open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        openList();
      } else if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
        handleTypeahead(key);
      }
      return;
    }
    switch (key) {
      case 'ArrowDown':
        event.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        move(-1);
        break;
      case 'Home':
        event.preventDefault();
        setActive(firstEnabled(0, 1));
        break;
      case 'End':
        event.preventDefault();
        setActive(firstEnabled(options.length - 1, -1));
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        choose(active);
        break;
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
      case 'Tab':
        setOpen(false);
        break;
      default:
        if (key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
          handleTypeahead(key);
        }
    }
  }

  return (
    <Field
      htmlFor={triggerId}
      label={label}
      hint={hint}
      error={error}
      required={required}
      full={full}
      describedById={describedById}
    >
      <div className={cx('bl-select', open && 'is-open')} ref={rootRef}>
        <button
          id={triggerId}
          type="button"
          className={cx(
            'bl-control',
            'bl-select__trigger',
            `bl-control--${size}`,
            full && 'bl-control--full',
            Boolean(error) && 'is-invalid',
            disabled && 'is-disabled',
          )}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={open && active >= 0 ? `${listboxId}-opt-${active}` : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedById}
          aria-label={ariaLabel}
          disabled={disabled}
          onClick={() => (open ? setOpen(false) : openList())}
          onKeyDown={handleKeyDown}
          onBlur={(event) => {
            if (!rootRef.current?.contains(event.relatedTarget as Node)) setOpen(false);
          }}
        >
          <span className={cx('bl-select__value', !selected && 'is-placeholder')}>
            {selected ? selected.label : placeholder}
          </span>
          <IconChevronDown className="bl-select__chevron" size={14} />
        </button>
        {open && (
          <div className="bl-popover">
            <ul className="bl-listbox" role="listbox" id={listboxId} aria-label={ariaLabel}>
              {options.map((option, i) => (
                <li
                  key={option.value}
                  id={`${listboxId}-opt-${i}`}
                  ref={(el) => {
                    if (el) optionRefs.current.set(i, el);
                    else optionRefs.current.delete(i);
                  }}
                  role="option"
                  aria-selected={option.value === value}
                  aria-disabled={option.disabled || undefined}
                  className={cx(
                    'bl-listbox__option',
                    i === active && 'is-active',
                    option.disabled && 'is-disabled',
                  )}
                  onPointerMove={() => !option.disabled && setActive(i)}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={() => choose(i)}
                >
                  <span>{option.label}</span>
                  {option.hint != null && <span className="bl-listbox__hint">{option.hint}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Field>
  );
}
